/**
 * WHICH PROJECT HOLDS A SESSION (RFC 0281 S2).
 *
 * An agent pane names a project, and the list it falls back to when you press
 * back is that project's registry. Everything that opened a session by id was
 * stamping the pane with `activeProjectId` — the focused pane's project, or the
 * window's — which is right only when the session happens to live there. Open a
 * session from a pane sitting on another project and the pane claimed the wrong
 * one: the thread rendered fine, because a session document is found by id, and
 * back then listed a project the session was never in.
 *
 * So: the same question the inbox already answers per row, asked by id. The
 * registry is the authority — a session belongs to the project that LISTS it.
 *
 * The cost is one project document per project in the World, and it is usually
 * already paid: the sidebar's directory and the inbox both hold the same set
 * open. A window with the sidebar collapsed and no inbox on screen pays it
 * alone, which is the registry read a session list makes anyway — no session is
 * hydrated here, only membership is read.
 *
 * NOT `useWorldSessions`. That list is sorted by recency and capped at
 * WORLD_INBOX_LIMIT, so resolving through it would answer "unknown" for exactly
 * the old sessions a person reaches for by link or by search. This index is
 * every session in every project, unsorted, because membership does not care
 * which is newest.
 */

import type { ProjectDoc } from '@mod/core/model'
import { useCallback, useMemo } from 'react'
import { useWorldProjects } from '../../services/world/useWorldProjects'
import { useProjectDocs } from '../../shell/spaces/useProjectCatalogs'

/** What the index reads per project: the registry, already hydrated. */
export interface SessionProjectSource {
  projectId: string
  doc: ProjectDoc | undefined
}

/**
 * sessionId -> the project whose registry lists it.
 *
 * FIRST WRITER WINS on the rare id listed by two projects. A session moved
 * between registries is written to the new one and removed from the old, so an
 * id in both is a half-applied move; picking either is a guess, and picking
 * deterministically at least makes the guess stable across renders.
 *
 * Pure, so the membership rule is testable without a repo.
 */
export function sessionProjectIndex(
  sources: SessionProjectSource[],
): Map<string, string> {
  const out = new Map<string, string>()
  for (const { projectId, doc } of sources) {
    if (!doc) continue
    const ids = (doc as { sessionIds?: string[] }).sessionIds ?? []
    for (const raw of ids) {
      const id = String(raw)
      if (!out.has(id)) out.set(id, projectId)
    }
  }
  return out
}

/**
 * Resolve a session's project, live.
 *
 * Returns null for an id no project lists, which the caller reads as "not
 * known here" and answers with its own project. Null rather than a fallback
 * baked in: a session whose project document has not synced yet is temporarily
 * unknown, and only the caller knows what it is willing to open instead.
 */
export function useSessionProject(
  /**
   * The window's own project, always read even when the World does not list it.
   * The registry can be empty for ordinary reasons — unsynced, pointer unset, a
   * project opened by link and never registered — and the sidebar keeps the
   * same rule for the directory. Without it, a one-project window resolves
   * nothing and every session falls back.
   */
  windowProjectId: string,
): (sessionId: string) => string | null {
  const projects = useWorldProjects()
  const projectIds = useMemo(() => {
    const ids = projects.map((p) => p.projectId)
    return ids.includes(windowProjectId) ? ids : [...ids, windowProjectId]
  }, [projects, windowProjectId])
  const docs = useProjectDocs(projectIds)

  const index = useMemo(
    () =>
      sessionProjectIndex(
        projectIds.map((projectId) => ({
          projectId,
          doc: docs.get(projectId),
        })),
      ),
    [projectIds, docs],
  )

  return useCallback(
    (sessionId: string) => index.get(sessionId) ?? null,
    [index],
  )
}
